import { useRef, useState, RefObject, Dispatch, SetStateAction } from 'react';
import randomString from '@/utils/random-string';

export type UsePopoverStateReturn = {
  id: string;
  // State
  open: boolean;
  closing: boolean;
  display: string;
  mountContent: boolean;
  slideFrom: 'top' | 'bottom' | undefined;
  hasOpenedAtLeastOnce: boolean;
  triggerBoundsRecalc: number;

  // State setters
  setOpen: Dispatch<SetStateAction<boolean>>;
  setClosing: Dispatch<SetStateAction<boolean>>;
  setDisplay: Dispatch<SetStateAction<string>>;
  setMountContent: Dispatch<SetStateAction<boolean>>;
  setSlideFrom: Dispatch<SetStateAction<'top' | 'bottom' | undefined>>;
  setHasOpenedAtLeastOnce: Dispatch<SetStateAction<boolean>>;
  setTriggerBoundsRecalc: Dispatch<SetStateAction<number>>;

  // Refs
  containerRef: RefObject<HTMLDivElement | null>;
  contentContainerRef: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
  triggerRef: RefObject<HTMLButtonElement | null>;
  portalId: string;

  // Previous state tracking
  previousState: RefObject<'open' | 'closed'>;
};

/**
 * Manages all state and refs for the Popover component.
 * Consolidates state management to reduce clutter in main component.
 */
export function usePopoverState({
  defaultOpen,
  propsId,
}: {
  defaultOpen: boolean;
  propsId?: string;
}): UsePopoverStateReturn {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const portalId = useRef(`popover-portal-${randomString(7)}`);

  const [open, setOpen] = useState(defaultOpen);
  const [closing, setClosing] = useState(false);
  const [display, setDisplay] = useState('none');
  const [mountContent, setMountContent] = useState(false);
  const [slideFrom, setSlideFrom] = useState<'top' | 'bottom' | undefined>();
  const [hasOpenedAtLeastOnce, setHasOpenedAtLeastOnce] = useState(false);
  const [triggerBoundsRecalc, setTriggerBoundsRecalc] = useState(0);

  const generatedId = useRef(randomString(7));
  const id = propsId || generatedId.current;

  // Important to prevent infinite loop calling of onOpen and onClose
  const previousState = useRef<'open' | 'closed'>(open ? 'open' : 'closed');

  return {
    id,

    // State
    open,
    closing,
    display,
    mountContent,
    slideFrom,
    hasOpenedAtLeastOnce,
    triggerBoundsRecalc,

    // State setters
    setOpen,
    setClosing,
    setDisplay,
    setMountContent,
    setSlideFrom,
    setHasOpenedAtLeastOnce,
    setTriggerBoundsRecalc,

    // Refs
    containerRef,
    contentContainerRef,
    contentRef,
    triggerRef,
    portalId: portalId.current,

    // Tracking
    previousState,
  };
}
